/* Claude 4.16 — THE BASKET DRAWER'S TOP PROMO SLOT (injector + clock).
 *
 * snippets/claude-drawer-promo.liquid renders an inert <template> carrying the
 * whole promo — markup, copy, prices, colours — plus the rules for showing it
 * as data- attributes. This file does two jobs and nothing else:
 *
 *   1. move that template into #ClaudeCartDrawer-Inner, directly under
 *      .ccd-header, whenever the rules say it should be there;
 *   2. tick the countdown, and take the whole thing down the second it runs out.
 *
 * ALL THE PROMO'S CONFIGURATION LIVES IN THE SNIPPET, NOT HERE. There is no
 * handle, date, percentage or piece of copy in this file, deliberately: the
 * point of 4.16 is that the next sale is one edit to one small file. If you
 * find yourself about to add a product handle below, put it in the snippet's
 * edit block instead.
 *
 * ============================================================
 * NOTHING HERE MAY ASSUME THE DOM SURVIVES A CART CHANGE.
 * swapSections() in claude-cart-drawer.js replaces #ClaudeCartDrawer-Inner
 * wholesale on every cart request, so anything injected is destroyed on every
 * tick, every stepper press and every quantity change. Hence a
 * MutationObserver rather than a one-shot paint on load — the same lesson the
 * drawer's error line (3.63), its discount note (3.85) and the add-on stepper
 * (4.10) each had to learn.
 * ============================================================
 *
 * THE CARD IS DRAWN, NOT CHOSEN. When the promo carries a tray of product
 * cards, the snippet renders EVERY eligible product and hides all but one; a
 * weighted draw below moves .is-pick on each opening of the drawer. Picking
 * client-side rather than in Liquid is the whole point: storefront pages are
 * CDN-cached, so a Liquid pick would show one shopper the same bag all day and
 * a whole region the same bag all morning. Weights come from
 * data-promo-weight, written by the snippet's edit block — no product handle
 * or probability appears in this file.
 *
 * IT DEFERS TO THE SECTION'S OWN BANNER. If sections/claude-cart-drawer.liquid
 * has rendered its "earned" .ccd-flashbanner — which requires a real ALLOCATED
 * discount on a real cart line — this injects nothing, so a shopper never sees
 * two bands. That path is dormant whenever the sale is run by cutting prices
 * rather than by an automatic discount, and costs nothing to keep.
 *
 * TWO EXPIRIES, BELT AND BRACES. The snippet renders no template at all past
 * its end date, so normally there is nothing here to do. But storefront pages
 * are CDN-cached, and a page cached at 11:58 could still be served at 12:05
 * with a live template inside it. The clock below is the second gate: at zero
 * it removes the promo and stops observing, whatever the HTML says.
 */
(function () {
  'use strict';

  var PAINT_DEBOUNCE = 60;
  var TICK_MS = 20000;
  var timer = null;
  var clockTimer = null;
  var cartCache = null; // { sig: string, hasBag: bool }
  var pendingRoll = true; // a fresh draw is owed (first paint, or the drawer just opened)

  function tpl() { return document.getElementById('ClaudeDrawerPromo'); }
  function innerEl() { return document.querySelector('#ClaudeCartDrawer-Inner'); }
  function drawerEl() { return document.querySelector('cart-drawer.ccd'); }
  function painted(inn) { return inn ? inn.querySelector('[data-ccd-promo]') : null; }

  function endDate(t) {
    var raw = t && t.getAttribute('data-promo-end');
    if (!raw) return null;
    var d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  function expired(t) {
    var d = endDate(t);
    return d ? d.getTime() - Date.now() <= 0 : false;
  }

  function strip(inn) {
    var el = painted(inn);
    if (el) el.remove();
  }

  /* ---------------------------------------------------------------
     WHO SEES IT.

     'items'  — anything in the basket. Read from the DOM (.ccd-item), so no
                network call: the drawer has already rendered the truth.
     'always' — everyone, empty basket included.
     'bags'   — needs product_type, which is not in the drawer's markup, so
                this is the one mode that costs a /cart.js read. Cached against
                a cheap signature of the basket so a burst of mutations does
                not become a burst of requests.
     --------------------------------------------------------------- */
  function cartSignature(inn) {
    var rows = inn.querySelectorAll('.ccd-item');
    var sig = String(rows.length);
    [].forEach.call(rows, function (r) {
      var q = r.querySelector('.ccd-qty-value');
      sig += '|' + (r.getAttribute('data-line') || '') + ':' + (q ? q.textContent.trim() : '');
    });
    return sig;
  }

  function fetchCart() {
    var d = drawerEl();
    if (d && typeof d.fetchCart === 'function') return Promise.resolve(d.fetchCart());
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  /* Returns true / false, or null meaning "ask again once the cart is known". */
  function gatePasses(t, inn, then) {
    var when = t.getAttribute('data-promo-when') || 'items';
    var hasItems = !!inn.querySelector('.ccd-item');

    if (when === 'always') return then(true);
    if (when !== 'bags') return then(hasItems);

    if (!hasItems) return then(false);

    var sig = cartSignature(inn);
    if (cartCache && cartCache.sig === sig) return then(cartCache.hasBag);

    fetchCart().then(function (cart) {
      if (!cart || !cart.items) return then(false);
      var hasBag = cart.items.some(function (i) {
        return String(i.product_type || '').toLowerCase() === 'bag';
      });
      cartCache = { sig: sig, hasBag: hasBag };
      then(hasBag);
    });
  }

  /* ---------------------------------------------------------------
     ALREADY IN THE BASKET? THEN DO NOT ADVERTISE IT.

     The drawer links every line to /products/<handle>, so this is a DOM read
     rather than a fetch. Links inside our own promo are skipped for the
     obvious reason: the card would otherwise see itself and conclude the
     shopper already owns the bag it is advertising.

     With a tray, an owned product is dropped from the draw and the others
     carry on; the promo only goes entirely when the basket holds every one of
     them. Without a tray there is nothing to swap to, so the band goes.
     --------------------------------------------------------------- */
  function ownedHandles(inn) {
    var owned = {};
    var links = [].slice.call(inn.querySelectorAll('a[href*="/products/"]'));
    links.forEach(function (a) {
      if (a.closest && a.closest('[data-ccd-promo]')) return;
      var href = String(a.getAttribute('href') || '').toLowerCase().split('?')[0].split('#')[0];
      var m = href.match(/\/products\/([^\/]+)/);
      if (m && m[1]) owned[m[1]] = true;
    });
    return owned;
  }

  function promoHandles(t) {
    return String(t.getAttribute('data-promo-handles') || '')
      .toLowerCase()
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /* Band-only styles (slim, or a card style with an empty handle list): there
     is nothing to swap to, so an owned product hides the lot. */
  function bandBlockedByOwnership(t, inn) {
    if (t.getAttribute('data-promo-hide-owned') !== '1') return false;
    var handles = promoHandles(t);
    if (!handles.length) return false;
    var owned = ownedHandles(inn);
    return handles.some(function (h) { return owned[h]; });
  }

  function weightOf(card) {
    var w = parseFloat(card.getAttribute('data-promo-weight'));
    return isFinite(w) && w > 0 ? w : 0;
  }

  /* THE DRAW. Returns false if the whole promo should come down. */
  function reroll() {
    var inn = innerEl();
    var wrap = painted(inn);
    var t = tpl();
    if (!inn || !wrap || !t) return true;

    var cards = [].slice.call(wrap.querySelectorAll('.cdp-card'));
    if (!cards.length) return true; // band-only style — nothing to draw

    var owned = t.getAttribute('data-promo-hide-owned') === '1' ? ownedHandles(inn) : {};
    var pool = cards.filter(function (c) {
      return !owned[String(c.getAttribute('data-promo-handle') || '').toLowerCase()];
    });
    if (!pool.length) { wrap.remove(); return false; }

    var total = 0;
    pool.forEach(function (c) { total += weightOf(c); });

    var chosen;
    if (total > 0) {
      var r = Math.random() * total;
      for (var i = 0; i < pool.length; i++) {
        r -= weightOf(pool[i]);
        if (r <= 0) { chosen = pool[i]; break; }
      }
    }
    /* Every weight zero or the float arithmetic landing past the end: fall
       back to a flat draw rather than to nothing. */
    if (!chosen) chosen = pool[Math.floor(Math.random() * pool.length)] || pool[0];

    cards.forEach(function (c) {
      var on = c === chosen;
      if (c.classList.contains('is-pick') !== on) c.classList.toggle('is-pick', on);
    });
    return true;
  }

  function paint() {
    var inn = innerEl();
    if (!inn) return;

    var t = tpl();
    if (!t || !t.content) { strip(inn); return; }
    if (expired(t)) { teardown(); return; }

    /* Never one of each: the section's own earned banner wins outright.
       Ours carries the wrapper attribute, so this only matches the earned one. */
    var earned = inn.querySelector('.ccd-flashbanner');
    if (earned && !earned.closest('[data-ccd-promo]')) { strip(inn); return; }

    /* Band-only styles decide ownership up front; tray styles decide it inside
       the draw, where an owned product is merely one fewer card. */
    if (!t.content.querySelector('.cdp-card') && bandBlockedByOwnership(t, inn)) { strip(inn); return; }

    gatePasses(t, inn, function (ok) {
      var live = innerEl();
      if (!live) return;
      if (!ok) { strip(live); return; }
      if (painted(live)) { // already there — do not re-inject, but a draw may be owed
        if (pendingRoll) { pendingRoll = false; if (!reroll()) return; }
        clock();
        return;
      }

      var header = live.querySelector('.ccd-header');
      if (!header) return;
      var wrap = document.createElement('div');
      wrap.setAttribute('data-ccd-promo', '');
      wrap.appendChild(t.content.cloneNode(true));
      header.insertAdjacentElement('afterend', wrap);
      pendingRoll = false;
      if (!reroll()) return;
      clock();
    });
  }

  /* ---------------------------------------------------------------
     THE CLOCK. Renders into whatever [data-promo-clock] elements the chosen
     style put in the markup, so adding a fourth style means adding markup,
     not code. Reaching zero removes the promo and stops everything.
     --------------------------------------------------------------- */
  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function clock() {
    var t = tpl();
    var d = endDate(t);
    if (!t || !d) return;

    var ms = d.getTime() - Date.now();
    if (ms <= 0) { teardown(); return; }

    var mins = Math.floor(ms / 60000);
    var days = Math.floor(mins / 1440);
    var hrs = Math.floor((mins % 1440) / 60);
    var mm = mins % 60;

    var long, short;
    if (days > 0) {
      long = days + 'd ' + pad(hrs) + 'h ' + pad(mm) + 'm';
      short = days + 'd ' + pad(hrs) + 'h';
    } else if (hrs > 0) {
      long = hrs + 'h ' + pad(mm) + 'm left';
      short = hrs + 'h ' + pad(mm) + 'm';
    } else {
      long = mm + ' min left';
      short = mm + 'm left';
    }

    var nodes = document.querySelectorAll('[data-ccd-promo] [data-promo-clock]');
    [].forEach.call(nodes, function (el) {
      var kind = el.getAttribute('data-promo-clock');
      if (kind === 'units') {
        setUnit(el, 'd', pad(days));
        setUnit(el, 'h', pad(hrs));
        setUnit(el, 'm', pad(mm));
      } else if (kind === 'short') {
        if (el.textContent !== short) el.textContent = short;
      } else if (el.textContent !== long) {
        el.textContent = long;
      }
    });
  }

  function setUnit(root, key, value) {
    var el = root.querySelector('[data-promo-u="' + key + '"]');
    if (el && el.textContent !== value) el.textContent = value;
  }

  function teardown() {
    var inn = innerEl();
    if (inn) strip(inn);
    var t = tpl();
    if (t) t.remove(); // so a later paint() finds nothing and no-ops
    clearInterval(clockTimer);
    clearTimeout(timer);
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(paint, PAINT_DEBOUNCE);
  }

  /* ---------------------------------------------------------------
     "A NEW BAG EVERY TIME I OPEN THE BASKET."

     claude-cart-drawer.js dispatches no event on open and does not re-render
     the inner HTML when it opens — open() only adds .ccd-open to <html>. So
     that class is the signal, watched on documentElement. Patching the custom
     element's open() would be the other option and was not taken: it would
     break silently the day the drawer is rewritten, whereas a class that
     stopped being added would break the drawer's own styling first and be
     noticed immediately.
     --------------------------------------------------------------- */
  function watchOpen() {
    var root = document.documentElement;
    var wasOpen = root.classList.contains('ccd-open');
    new MutationObserver(function () {
      var isOpen = root.classList.contains('ccd-open');
      if (isOpen && !wasOpen) { pendingRoll = true; schedule(); }
      wasOpen = isOpen;
    }).observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  function start() {
    if (!tpl()) return; // no live promo on this page — do nothing at all
    var d = drawerEl();
    if (d) new MutationObserver(schedule).observe(d, { childList: true, subtree: true });
    watchOpen();
    clockTimer = setInterval(clock, TICK_MS);
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
